import { FC, useContext, useRef, useState } from "react";
import { FlexContent, Numeric, Table, TableCell, TableHeadCell, TableRow } from "@rothoblaas/design-system";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useI18n from "../../../../hooks/useTranslations";
import { GammaMValue } from "../../../../http/screwConnector/types/normativeTables";
import { useScrewConnectorGammaMValuesApi } from "../../../../http/screwConnector/useAdminNormativeTables";

// gamma_m_values has exactly 2 fixed rows (EC5 / NTC2018) — a simple inline-edit list is a better
// fit here than a separate add/edit modal form.
const AdminGammaMValues: FC = () => {
  const i18n = useI18n();
  const t = i18n.translation.screwConnectorModule.normativeTables.gammaM;
  const dispatch = useContext(NotificationDispatch);
  const aborter = useRef(new AbortController());
  const api = useScrewConnectorGammaMValuesApi();
  const { data } = api.useGammaMValues({ limit: 10 }, aborter.current.signal);
  const [pending, setPending] = useState<Record<string, { columnA?: number | null; columnB?: number | null }>>({});

  const commit = async (item: GammaMValue, patch: Partial<Pick<GammaMValue, "columnA" | "columnB">>) => {
    const columnA = patch.columnA ?? item.columnA;
    const columnB = patch.columnB ?? item.columnB;
    if (columnA == null || columnB == null) return;
    try {
      await api.updateGammaMValue(item.standard, { columnA, columnB });
      dispatch(showSuccessNotification(i18n.translation));
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  if (!data) {
    return null;
  }

  return (
    <FlexContent padding>
      <Table>
        <thead>
          <TableRow>
            <TableHeadCell align="left">{t.fields.standard}</TableHeadCell>
            <TableHeadCell align="left">{t.fields.columnA}</TableHeadCell>
            <TableHeadCell align="left">{t.fields.columnB}</TableHeadCell>
          </TableRow>
        </thead>
        <tbody>
          {data.items.map((item) => (
            <TableRow key={item.standard}>
              <TableCell>{item.standard}</TableCell>
              <TableCell>
                <Numeric
                  value={pending[item.standard]?.columnA ?? item.columnA}
                  decimalScale={3}
                  onValueChange={(value) =>
                    setPending((prev) => ({ ...prev, [item.standard]: { ...prev[item.standard], columnA: value } }))
                  }
                  onBlur={(value) => commit(item, { columnA: value ?? undefined })}
                />
              </TableCell>
              <TableCell>
                <Numeric
                  value={pending[item.standard]?.columnB ?? item.columnB}
                  decimalScale={3}
                  onValueChange={(value) =>
                    setPending((prev) => ({ ...prev, [item.standard]: { ...prev[item.standard], columnB: value } }))
                  }
                  onBlur={(value) => commit(item, { columnB: value ?? undefined })}
                />
              </TableCell>
            </TableRow>
          ))}
        </tbody>
      </Table>
    </FlexContent>
  );
};

export default AdminGammaMValues;
